import { useHomeStore } from '../../store/home-store';
import { EventCard } from '../event-card';
import { MarketCard, MarketCardSkeleton } from '../market-card';
import { hotThreshold, isHotVolume } from '../market-card/hot';
import { LightningEmpty } from './watch-list-empty';
import { groupMarketsByEvent } from './event-groups';
import { BlockLoading } from '@/client/components/fetch-status/block-loading';
import { cn } from '@/client/utils';
import { api } from '@/client/utils/api';
import { useMarkets } from '@/libs/react-sdk/api/use-markets';
import { useAppSettingStore } from '@/client/store/app-store';
import { MarketsParams, MarketsSortType } from '@/libs/sdk/api';
import { CLOB_ENABLED, DEFAULT_BASE_TOKEN } from '@/client/confs/app';
import { useClobMarketsAsMarket } from '@/client/clob/hooks';
import { useMemo, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useMedia from 'react-use/lib/useMedia';
import useInViewport from 'ahooks/lib/useInViewport';

type MarketCardListSkeletonProps = { len?: number };

export const MarketCardListSkeleton = ({ len = 1 }: MarketCardListSkeletonProps) => {
  return Array.from({ length: len }).map((_, index) => <MarketCardSkeleton key={`MarketGridCardSkeleton-${index}`} />);
};

export const TheMarketCardList = () => {
  const { t } = useTranslation(['home']);
  const isDesktop = useMedia('(min-width: 768px)');
  const ref = useRef<HTMLDivElement>(null);
  const pageSize = useMemo(() => (isDesktop ? 16 : 10), [isDesktop]);
  const category = useHomeStore(state => state.category);
  const tag = useHomeStore(state => state.tag);
  const sortDirection = useHomeStore(state => state.sortDirection);
  const sortType = useHomeStore(state => state.sortType);
  const lensTokenAddress = useAppSettingStore(state => state.baseToken.address);
  // Non-default currency lens (e.g. AIP) with an empty feed = that collateral has
  // no markets yet; show a currency-scoped empty state instead of generic "nothing yet".
  const isNonDefaultLens = lensTokenAddress.toLowerCase() !== DEFAULT_BASE_TOKEN.address.toLowerCase();
  const searchParams = useMemo(() => {
    // Feed is scoped to the global currency lens — one collateral at a time.
    const params: MarketsParams = { page: 1, pageSize, baseTokenAddress: lensTokenAddress };
    if (tag) params.tag = tag;
    switch (category) {
      case 'trending':
        params.minVolumeTrending = true;
        break;
      case 'new':
        params.newMarket = true;
        break;
      case 'live':
        params.soonExpired = true;
        break;
      case 'watchlist':
        params.followed = true;
        break;
      default:
        if (category) params.categoryId = category;
    }
    switch (sortType) {
      case 'expire-time':
        params.sortType = MarketsSortType.expireTime;
        break;
      case 'volume':
        params.sortType = MarketsSortType.volume;
        break;
    }
    switch (sortDirection) {
      case 'asc':
        params.sortAsc = true;
        break;
      case 'desc':
        params.sortAsc = false;
        break;
    }
    return params;
  }, [category, pageSize, sortDirection, sortType, tag, lensTokenAddress]);
  const { data, isLoading, isFetching, isRefetching, hasNextPage, isFetchingNextPage, fetchNextPage } = useMarkets(
    api,
    searchParams
  );
  const apmmMarkets = useMemo(() => data?.pages.flatMap(item => item.markets) ?? [], [data]);

  // Merge CLOB markets into the same list
  const { data: clobMarkets, isLoading: clobLoading } = useClobMarketsAsMarket();
  const marketList = useMemo(() => {
    if (!CLOB_ENABLED) return apmmMarkets;
    return [...clobMarkets, ...apmmMarkets];
  }, [apmmMarkets, clobMarkets]);

  const groupedMarkets = useMemo(() => groupMarketsByEvent(marketList), [marketList]);
  const hotVolume = useMemo(() => hotThreshold(marketList.map(m => Number(m.marketVolume))), [marketList]);
  const isEmpty = !hasNextPage && marketList.length === 0 && clobMarkets.length === 0;

  // Infinite scroll sentinel
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [inViewport] = useInViewport(sentinelRef);

  useEffect(() => {
    if (inViewport && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inViewport, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Track filter changes for transition animation
  const [isTransitioning, setIsTransitioning] = useState(false);
  const prevFilterRef = useRef({ category, tag, sortType, sortDirection });
  
  useEffect(() => {
    const prev = prevFilterRef.current;
    const hasFilterChanged = 
      prev.category !== category || 
      prev.tag !== tag || 
      prev.sortType !== sortType || 
      prev.sortDirection !== sortDirection;
    
    if (hasFilterChanged) {
      setIsTransitioning(true);
      prevFilterRef.current = { category, tag, sortType, sortDirection };
      // Reset transition state after animation
      const timer = setTimeout(() => setIsTransitioning(false), 150);
      return () => clearTimeout(timer);
    }
  }, [category, tag, sortType, sortDirection]);
  
  // Show skeleton during initial load
  const showSkeleton = isLoading || (isFetching && marketList.length === 0);
  // Only show empty state when not loading and truly empty
  const showEmpty = !isLoading && !isFetching && isEmpty;
  
  return (
    <section className="px-4 md:px-6 py-4">
      {/* Section header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold">{t('all-markets', 'All Markets')}</h2>
        <span className={cn(
          "text-xs text-muted-foreground transition-opacity duration-200",
          (isTransitioning || showSkeleton) && "opacity-50"
        )}>
          {marketList.length > 0 && `${marketList.length}+ ${t('markets', 'markets')}`}
        </span>
      </div>
      
      <div
        ref={ref}
        className={cn(
          "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3",
          "transition-opacity duration-200 ease-out",
          isTransitioning && "opacity-60"
        )}
      >
        {/* Show skeleton during initial load or refetch with no data */}
        {showSkeleton && <MarketCardListSkeleton len={pageSize} />}
        
        {/* Show market cards when we have data */}
        {!showSkeleton &&
          groupedMarkets.map((group, index) =>
            group.markets.length > 1 ? (
              <EventCard key={`event-${group.eventId}-${index}`} eventId={group.eventId} markets={group.markets} />
            ) : (
              <MarketCard
                key={`market-${group.markets[0].marketAddress}-${index}`}
                market={group.markets[0]}
                index={index}
                hot={isHotVolume(Number(group.markets[0].marketVolume), hotVolume)}
              />
            )
          )}
        
        {/* Show loading skeleton when fetching next page */}
        {isFetchingNextPage && <MarketCardListSkeleton len={pageSize} />}
      </div>
      
      {/* Empty state - only show when not loading and truly empty */}
      {showEmpty &&
        (category == 'watchlist' || category == 'live' ? (
          <LightningEmpty
            title={category == 'watchlist' ? t('watchlist') : t('nothing-right-now')}
            desc={
              category == 'watchlist'
                ? t('follow-predictions-you-care-about')
                : t('markets-ending-within-48-hours-will-appear-here')
            }
          />
        ) : isNonDefaultLens ? (
          <LightningEmpty
            title={t('no-markets-for-currency', 'No markets in this currency yet')}
            desc={t('no-markets-for-currency-desc', 'Markets in this currency are coming soon.')}
            showButton={false}
          />
        ) : (
          <LightningEmpty title={t('translation:nothing-yet')} showButton={false} />
        ))}
        
      {hasNextPage && <div ref={sentinelRef} className="h-1" />}
      {isFetchingNextPage && <MarketCardListSkeleton len={pageSize} />}
    </section>
  );
};
